/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpEmailFields.tsx
 * FILE_ID: JOBHUB-QA-ITP-EMAIL-FIELDS-001
 * PURPOSE: Email fields for ITP Export.
 * Reused from QaItpEmailDialog (moved verbatim) so Export keeps every field the Email dialog had.
 */
import { useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { X } from 'lucide-react'
import ComboField from '@quotech/quote-builder/components/ComboField'
import {
  type QuoteClientHit,
  type useJobQuoteDetails,
} from '../useJobQuoteDetails'

type ExportDetails = Pick<
  ReturnType<typeof useJobQuoteDetails>,
  | 'jobTitle'
  | 'setJobTitle'
  | 'commitJobTitle'
  | 'clientReset'
  | 'clientName'
  | 'searchClients'
  | 'selectClient'
  | 'createClient'
  | 'reportTitle'
  | 'setReportTitle'
  | 'commitReportTitle'
  | 'error'
>
import { confirmRemoveImage } from './itpPreviewImageRemove'
import { QaItpOnedriveApproval } from './QaItpOnedriveApproval'
import { QaItpRecipientsField } from './QaItpRecipientsField'
import { QaCoverSizeFields } from '../QaCoverSizeFields'
import { QaItpIncludeFields } from '../QaItpIncludeFields'
import { ITP_COVER_IMAGE_DEFAULT_MM } from '../../../qa/itpCover'
import type { useQaItpFrom } from './useQaItpFrom'
import type { OnedriveApproval } from './useQaItpOnedrive'

const INPUT = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm'

function clientLabel(hit: QuoteClientHit): string {
  return (hit.client_name || hit.company_name || '').trim()
}

export function QaItpEmailFields({
  details,
  to,
  cc,
  bcc,
  subject,
  body,
  setTo,
  setCc,
  setBcc,
  setSubject,
  setBody,
  error,
  fromModel,
  attachmentName = '',
  setAttachmentName,
  shareOnedrive = false,
  setShareOnedrive,
  onedriveApproval = null,
  onCopyAdminLink,
  onSignInOnedrive,
  onUseAttachment,
  coverUrl = null,
  coverBusy = false,
  onChooseCover,
  onRemoveCover,
  coverHeightMm,
  coverWidthMm,
  onCoverHeightMm,
  onCoverWidthMm,
  onCommitCoverSize,
  include,
  onIncludeChange,
}: {
  details: ExportDetails
  to: string
  cc: string
  bcc: string
  subject: string
  body: string
  setTo: (value: string) => void
  setCc: (value: string) => void
  setBcc: (value: string) => void
  setSubject: (value: string) => void
  setBody: (value: string) => void
  error: string | null
  fromModel: ReturnType<typeof useQaItpFrom>
  attachmentName?: string
  setAttachmentName?: (value: string) => void
  shareOnedrive?: boolean
  setShareOnedrive?: (value: boolean) => void
  onedriveAllowed?: boolean
  onedriveApproval?: OnedriveApproval | null
  onCopyAdminLink?: () => void
  onSignInOnedrive?: () => void
  onUseAttachment?: () => void
  coverUrl?: string | null
  coverBusy?: boolean
  onChooseCover?: (file: File | null) => void
  onRemoveCover?: () => Promise<boolean>
  coverHeightMm?: number
  coverWidthMm?: number
  onCoverHeightMm?: (value: number) => void
  onCoverWidthMm?: (value: number) => void
  onCommitCoverSize?: () => void
  include?: { product: boolean; requirement: boolean; acceptance: boolean }
  onIncludeChange?: (key: 'product' | 'requirement' | 'acceptance', next: boolean) => void
}) {
  const coverInput = useRef<HTMLInputElement>(null)
  const onEnter = (event: ReactKeyboardEvent<HTMLInputElement>, commit: () => void) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    commit()
  }

  return (
    <div className="space-y-3">
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Job title</span>
        <input
          className={INPUT}
          value={details.jobTitle}
          placeholder="Job title"
          onChange={(event) => details.setJobTitle(event.target.value)}
          onBlur={details.commitJobTitle}
          onKeyDown={(event) => onEnter(event, details.commitJobTitle)}
        />
      </label>
      <ComboField<QuoteClientHit>
        key={details.clientReset}
        label="Client"
        placeholder="Search clients…"
        revertUnmatchedOnBlur
        value={details.clientName}
        inputClassName={INPUT}
        className="min-w-0"
        onCommitText={() => undefined}
        search={details.searchClients}
        getKey={(hit) => hit.id}
        getDisplay={clientLabel}
        renderItem={(hit) => clientLabel(hit)}
        onSelect={(hit) => {
          void details.selectClient(hit)
          const email = (hit.email || hit.primary_email || '').trim()
          if (email) setTo(email)
        }}
        onCreate={details.createClient}
      />
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Report title</span>
        <input
          className={INPUT}
          value={details.reportTitle}
          onChange={(event) => details.setReportTitle(event.target.value)}
          onBlur={details.commitReportTitle}
          onKeyDown={(event) => onEnter(event, details.commitReportTitle)}
        />
      </label>
      <div className="block min-w-0 text-sm">
        <span className="mb-1 block">Cover image</span>
        {coverUrl ? (
          <span className="relative mb-2 block">
            <img src={coverUrl} alt="" className="max-h-24 w-full object-contain" />
            <button
              type="button"
              aria-label="Remove image"
              className="absolute right-1 top-1 z-10 flex h-11 w-11 items-center justify-center border-0 bg-transparent p-0 md:h-6 md:w-6"
              onPointerDown={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                // Reused from: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpExportView.tsx (back)
                if (!confirmRemoveImage('cover')) return
                void onRemoveCover?.().then((ok) => {
                  if (ok && coverInput.current) coverInput.current.value = ''
                })
              }}
            >
              <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background">
                <X className="h-3 w-3 text-destructive" aria-hidden />
              </span>
            </button>
          </span>
        ) : null}
        <label className="block min-w-0">
          <input
            ref={coverInput}
            className={INPUT}
            type="file"
            accept="image/*"
            aria-label="Cover image"
            disabled={coverBusy}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) onChooseCover?.(file)
              event.target.value = ''
            }}
          />
        </label>
        {coverBusy ? <span className="mt-1 block text-xs text-muted-foreground">Uploading cover photo…</span> : null}
        {coverUrl && !coverBusy ? (
          <button type="button" className="mt-1 text-xs underline" onClick={() => onChooseCover?.(null)}>
            Clear
          </button>
        ) : null}
      </div>
      {onCoverHeightMm && onCoverWidthMm ? (
        <QaCoverSizeFields
          inputClassName={INPUT}
          heightMm={coverHeightMm ?? ITP_COVER_IMAGE_DEFAULT_MM.height}
          widthMm={coverWidthMm ?? ITP_COVER_IMAGE_DEFAULT_MM.width}
          onHeightMm={onCoverHeightMm}
          onWidthMm={onCoverWidthMm}
          onCommit={() => onCommitCoverSize?.()}
        />
      ) : null}
      {include && onIncludeChange ? (
        <QaItpIncludeFields value={include} onChange={onIncludeChange} />
      ) : null}
      <label className="block min-w-0 text-sm">
        <span className="mb-1 flex items-center justify-between gap-2">
          <span>From</span>
          {fromModel.isDefault ? <span className="text-xs text-muted-foreground">Your default</span> : null}
        </span>
        <select
          aria-label="From"
          className={`${INPUT} min-h-[44px] md:min-h-0`}
          value={fromModel.from}
          onChange={(event) => fromModel.setFrom(event.target.value)}
        >
          {fromModel.sendable.map((account) => (
            <option key={account.id} value={`account:${account.id}`}>
              {account.display_name ? `${account.display_name} (${account.email_address})` : account.email_address}
            </option>
          ))}
          <option value="quotech">Quotech mail (hello@quotech.ai)</option>
        </select>
        {fromModel.from === 'login' ? (
          <span className="mt-1 block text-xs text-muted-foreground">
            Sent by Quotech mail on your behalf. Replies go to {fromModel.userEmail}.
          </span>
        ) : null}
        {fromModel.isDefault ? null : (
          <button
            type="button"
            className="mt-1 text-xs font-medium text-primary"
            disabled={fromModel.saving}
            onClick={() => { void fromModel.saveDefault() }}
          >
            {fromModel.saving ? 'Saving…' : 'Set as my default'}
          </button>
        )}
      </label>
      <QaItpRecipientsField label="Recipient email" value={to} onChange={setTo} placeholder="client@example.com" />
      <QaItpRecipientsField label="CC (comma-separated)" value={cc} onChange={setCc} />
      <QaItpRecipientsField label="BCC (comma-separated)" value={bcc} onChange={setBcc} />
      <label className="block text-sm">
        <span className="mb-1 block">Subject</span>
        <input className={INPUT} value={subject} onChange={(event) => setSubject(event.target.value)} />
      </label>
      <label className="block min-w-0 text-sm">
        <span className="mb-1 block">Attachment name</span>
        <input
          aria-label="Attachment name"
          className={INPUT}
          value={attachmentName}
          onChange={(event) => setAttachmentName?.(event.target.value)}
        />
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={shareOnedrive}
          onChange={(event) => setShareOnedrive?.(event.target.checked)}
        />
        <span>
          Share as OneDrive link
          <span className="mt-0.5 block text-xs text-muted-foreground">
            Only the To, CC and BCC people can open it, after signing in with their email.
          </span>
        </span>
      </label>
      {onedriveApproval ? (
        <QaItpOnedriveApproval
          approval={onedriveApproval}
          onCopyLink={() => onCopyAdminLink?.()}
          onSignIn={() => onSignInOnedrive?.()}
          onUseAttachment={() => onUseAttachment?.()}
        />
      ) : null}
      <label className="block text-sm">
        <span className="mb-1 flex items-center justify-between gap-2">
          <span>Message</span>
          {fromModel.savedMessage && body === fromModel.savedMessage ? (
            <span className="text-xs text-muted-foreground">Your default</span>
          ) : null}
        </span>
        <textarea className={`${INPUT} min-h-28 resize-y`} rows={6} value={body} onChange={(event) => setBody(event.target.value)} />
        {fromModel.savedMessage !== '' && body === fromModel.savedMessage ? null : (
          <button
            type="button"
            className="mt-1 text-xs font-medium text-primary"
            disabled={fromModel.savingMessage || body.trim() === ''}
            onClick={() => { void fromModel.saveDefaultMessage(body) }}
          >
            {fromModel.savingMessage ? 'Saving…' : 'Set as my default message'}
          </button>
        )}
      </label>
      {error || details.error ? (
        <p role="alert" className="text-xs text-destructive">{error || details.error}</p>
      ) : null}
    </div>
  )
}
